import { useState } from 'react';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

// Contact form for the landing page. Submissions are written to the
// `contactMessages` Firestore collection and read back in the /admin inbox —
// there is no email/SMTP dependency (by design). Firestore rules allow anyone
// to CREATE a message (shape-constrained) but only the admin to read them.
//
// Styled for the landing page's dark theme.

const CARD = 'rgba(255,255,255,0.04)';
const CARD_BORDER = 'rgba(255,255,255,0.09)';
const TEXT = '#e5e7f0';
const MUTED = '#9aa3b8';
const INDIGO = '#6366f1';
const VIOLET = '#a855f7';

const inputStyle = {
  width: '100%', boxSizing: 'border-box', padding: '12px 14px', borderRadius: 10,
  background: 'rgba(255,255,255,0.03)', border: `1px solid ${CARD_BORDER}`,
  color: TEXT, fontSize: 14, fontFamily: 'inherit', outline: 'none',
};

function ContactForm() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error

  const submit = async e => {
    e.preventDefault();
    if (!message.trim()) return;
    setStatus('sending');
    try {
      await addDoc(collection(db, 'contactMessages'), {
        name: name.trim().slice(0, 120),
        email: email.trim().slice(0, 200),
        message: message.trim().slice(0, 5000),
        userAgent: String(navigator.userAgent || '').slice(0, 400),
        handled: false,
        createdAt: serverTimestamp(),
      });
      setStatus('sent');
      setName(''); setEmail(''); setMessage('');
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" style={{ maxWidth: 1100, margin: '0 auto', padding: '40px 32px 20px' }}>
      <style>{`
        .cf-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
        @media (max-width: 640px) { .cf-row { grid-template-columns: 1fr; } }
      `}</style>
      <h2 style={{ fontSize: 'clamp(24px, 4vw, 34px)', fontWeight: 800, letterSpacing: '-0.6px', textAlign: 'center', margin: '0 0 10px', color: TEXT }}>
        Get in touch
      </h2>
      <p style={{ fontSize: 15, color: MUTED, textAlign: 'center', margin: '0 auto 28px', lineHeight: 1.6, maxWidth: 460 }}>
        Questions, feedback, or a tool you wish existed? Send a note — it goes straight to the developer.
      </p>

      {status === 'sent' ? (
        <div style={{
          textAlign: 'center', padding: '28px 24px', borderRadius: 16,
          background: 'rgba(52,211,153,.10)', border: '1px solid rgba(52,211,153,.30)', color: '#6ee7b7',
        }}>
          <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 6 }}>Thanks — message received.</div>
          <div style={{ fontSize: 14, color: MUTED }}>I'll get back to you if a reply is needed.</div>
          <button
            onClick={() => setStatus('idle')}
            style={{ marginTop: 16, background: 'transparent', border: `1px solid ${CARD_BORDER}`, color: TEXT, padding: '8px 16px', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
          >
            Send another
          </button>
        </div>
      ) : (
        <form onSubmit={submit} style={{
          display: 'flex', flexDirection: 'column', gap: 14,
          padding: '26px 24px', borderRadius: 18, background: CARD, border: `1px solid ${CARD_BORDER}`,
        }}>
          <div className="cf-row">
            <input style={inputStyle} type="text" placeholder="Name (optional)" value={name}
              maxLength={120} onChange={e => setName(e.target.value)} />
            <input style={inputStyle} type="email" placeholder="Email (optional, for a reply)" value={email}
              maxLength={200} onChange={e => setEmail(e.target.value)} />
          </div>
          <textarea style={{ ...inputStyle, minHeight: 130, resize: 'vertical' }} placeholder="Your message…"
            value={message} maxLength={5000} required onChange={e => setMessage(e.target.value)} />
          {status === 'error' && (
            <div style={{ fontSize: 13, color: '#fca5a5' }}>
              Something went wrong sending that. Please try again.
            </div>
          )}
          <button
            type="submit"
            disabled={status === 'sending' || !message.trim()}
            style={{
              alignSelf: 'flex-start', marginTop: 2,
              padding: '13px 28px', borderRadius: 12, border: 'none',
              fontSize: 15, fontWeight: 700, color: '#fff',
              background: `linear-gradient(135deg, ${INDIGO}, ${VIOLET})`,
              cursor: status === 'sending' || !message.trim() ? 'default' : 'pointer',
              opacity: status === 'sending' || !message.trim() ? 0.6 : 1,
              boxShadow: '0 8px 30px rgba(99,102,241,0.35)',
            }}
          >
            {status === 'sending' ? 'Sending…' : 'Send message'}
          </button>
          <p style={{ fontSize: 12, color: '#6b7280', margin: '2px 0 0', lineHeight: 1.5 }}>
            Only what you type here is sent. No case data, files, or tool activity is ever included.
          </p>
        </form>
      )}
    </section>
  );
}

export default ContactForm;
